import { formatValue, lengthLimit, showLoader, printWorkAround } from '../data-viz-helpers.js'

customElements.define('zero-comparison', class extends HTMLElement {
    static observedAttributes = ['data-values', 'data-labels', 'data-bg', 'data-fill', 'data-fill2', 'data-heading', 'data-format']

    constructor() {
        super().attachShadow({mode: 'open'})
    }

    connectedCallback() {
        this.getProps()
        this.shadowRoot.innerHTML = showLoader([this.dataset.values], this.render())
    }

    attributeChangedCallback(name, oldVal, newVal) {
        if(oldVal !== newVal) {
            this.getProps()
            this.shadowRoot.innerHTML = this.render()
        }
    }

    getProps() {
        this.height = 190
        this.width = 400
        this.bg = this.dataset.bg ?? 'var(--color-4-lt)'
        this.fill = this.dataset.fill ?? 'var(--color-4-dk)'
        this.fill2 = this.dataset.fill2 ?? 'var(--color-4)'
        this.labels = this.dataset.labels?.split(/,\s?/) ?? []
        this.values = this.dataset.values?.split(/,\s?/) ?? []
        this.heading = this.dataset.heading ?? ''
        this.format = this.dataset.format ?? ''
        this.padding = 30
        this.gap = 20
        this.labelSpace = 50
        this.gridLineCount = 4
        this.gridLineHeight = 1
        this.baselineHeight = 3
        this.topline = 20
        this.baseline = this.height - 5 - this.labelSpace
        this.innerWidth = this.width - (this.padding * 2)
        this.innerHeight = this.baseline - this.topline
        this.maxVal = Math.max(...this.values)
        this.count = 2
        this.barHeights = []
    }

    createHeading() {
        return `
            <p class="heading">${lengthLimit(this.heading, 22)}</p>
        `
    }

    createBaseline() {
        return `
            <rect 
                x="${this.padding}" 
                y="${this.baseline}" 
                fill="white" 
                height="${this.baselineHeight}" width="${this.innerWidth}" />
        `
    }

    createBarsAndLabels() {
        const bars = []

        this.values.forEach((val, idx) => {
            const height = val * (this.innerHeight / this.maxVal)
            const width = (this.innerWidth - (this.gap * (this.count - 1))) / this.count
            const x = (this.padding) + (width * idx)  + (this.gap * idx)
            const y = this.topline + (this.innerHeight - height)
            const fill = (idx === 1) ? this.fill2 : this.fill
            
            this.barHeights.push(height)

            bars.push(`
                <rect 
                    x="${x}" y="${y}" 
                    width="${width}" 
                    height="${height}" 
                    fill="${fill}" 
                    rx="10" 
                    title="${formatValue(val, this.format)}" />

                <text 
                    x="${x + (width / 2)}" 
                    y="${this.baseline - (height / 2)}" 
                    alignment-baseline="middle"
                    text-anchor="middle" 
                    fill="white" 
                    style="font-family: Raleway; font-size: 20px; font-weight: 800;">${formatValue(val, this.format)}</text>

                <text 
                    x="${x + (width / 2)}" 
                    y="${y + height + 30}" 
                    text-anchor="middle" 
                    fill="${fill}" 
                    style="font-family: Raleway; font-size: 14px; font-weight: 700;">${lengthLimit(this.labels[idx], 14)}</text>
            `)
        })

        return bars.join('')
    }

    createDifferenceBar() {
        const diff = this.values[1] - this.values[0]
        const val = (diff / Math.max(...this.values)) * 100
        let height = Math.max(...this.barHeights) - Math.min(...this.barHeights) - (this.gap / 4)
        const width = (this.innerWidth / this.count) + (this.gap / 4)
        const x = (diff < 0) ? this.padding + (width - this.gap / 2) : this.padding
        const y = this.topline
        const fill = (diff < 0) ? 'var(--color-neg)' : 'var(--color-pos)'
        const color = (height > 30) ? 'var(--neutral-5)' : 'var(--color-1)'
        let labelY = (height > 30) ? (y + height) - (height / 2) : y - 10
        const type = (diff < 0) ? '(decrease)' : '(increase)'

        // Deal with a small difference amount and 0 diff
        if(height < 5) {
            height = 1
        }
        
        if(diff === 0) {
            labelY = -20
            height = 0
        }
        
        return `
            <rect 
                x="${x}" y="${y}" 
                width="${width}" 
                height="${height}" 
                fill="${fill}" 
                title="${formatValue(val / 100, 'percent')}"
                rx="2" />

            <text 
                x="${x + (width / 2)}" 
                y="${labelY}" 
                text-anchor="middle" 
                alignment-baseline="middle"
                fill="${color}" 
                style="font-family: Raleway; font-size: 14px; font-weight: 800;">${formatValue(val / 100, 'percent')} ${type}</text>
        `
    }

    render() {
        return `
            <style>
                * {
                    box-sizing: border-box;
                }

                :host {
                    background-color: ${this.bg};
                    border-radius: var(--chart-radius);
                    container-type: inline-size;
                    display: grid;
                    height: 100%;
                    overflow: clip;
                    place-items: center;
                    position: relative;
                    -webkit-user-select: none;
                    user-select: none;
                    width: 100%;
                }

                .heading {
                    color: ${this.fill2};
                    font-size: 6cqw;
                    font-weight: 800;
                    text-align: center;
                }

                .chart {
                    display: block;
                    height: auto;
                    width: 100%;
                }
            </style>

            ${printWorkAround(this)}

            ${this.createHeading()}

            <svg 
                class="chart"
                xmlns="http://www.w3.org/2000/svg" 
                version="1.1" 
                viewBox="0 0 ${this.width} ${this.height}">
                ${this.createBaseline()}
                ${this.createBarsAndLabels()}
                ${this.createDifferenceBar()}
            </svg>

            ${(this.hasAttribute('data-help')) ? `<zero-help-button msg="${this.dataset.help}" color="${this.fill.replace('-dk', '')}"></zero-help-button>` : ''}
        `
    }
})